import { AppShellMain, AppShell as BaseAppShell } from "@mantine/core"
import {
  IconAdjustments,
  IconFileDollar,
  IconLogout,
  IconUserEdit,
} from "@tabler/icons-react"
import {
  AppLanguageMenu,
  AppHeader,
  AppNavBar,
  IconDashboard,
  IconRegulations,
  IconProducts,
  IconSuppliers,
  IconCertificates,
  IconDataTransfer,
  IconSettingsNavBar,
  IconFAQ,
  UserMenu,
  MenuItem,
} from "@tfg/ui"
import i18n from "i18next"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Outlet, useNavigate } from "react-router-dom"

import { Locale } from "@tfg/types/schemas/enum"

import { BRAND_LOCALES } from "~/i18n"
import { useAuth } from "~/store/auth-context.tsx"
import { findLanguage, findLanguageFlag } from "~/utils/languages.tsx"

export const useGetNavMenuItems = (type: "brand" | "supplier") => {
  const { t } = useTranslation()

  if (type === "brand")
    return [
      {
        link: "/",
        label: t("menus.dashboard"),
        Icon: IconDashboard,
      },
      {
        link: "regulations",
        label: t("menus.regulations"),
        Icon: IconRegulations,
      },
      {
        link: "products",
        label: t("menus.products"),
        Icon: IconProducts,
      },
      {
        link: "suppliers",
        label: t("menus.suppliers"),
        Icon: IconSuppliers,
      },
      {
        link: "documents",
        label: t("menus.certificates"),
        Icon: IconCertificates,
      },
      {
        link: "data-transfer",
        label: t("menus.dataTransfer"),
        Icon: IconDataTransfer,
      },
      {
        link: "settings",
        label: t("menus.settings"),
        Icon: IconSettingsNavBar,
      },
    ]
  if (type === "supplier")
    return [
      {
        link: "/",
        label: t("menus.dashboard"),
        Icon: IconDashboard,
      },
    ]
  return []
}

export const SETTINGS_MENU_ITEMS = [
  {
    link: "faq",
    label: i18n.t("menus.faq"),
    Icon: IconFAQ,
  },
]
export const SUPPLIER_MENU_ITEMS = [
  {
    link: "/",
    label: i18n.t("menus.dashboard"),
    Icon: IconDashboard,
  },
]
export const BrandAppShell = () => {
  const { t } = useTranslation()
  const [currentLocale, setCurrentLocale] = useState<string>(i18n.language)
  const { user, logout, isAdmin } = useAuth()
  const isClient = !!user?.organization
  const navigate = useNavigate()

  const navItems = useGetNavMenuItems(isClient ? "brand" : "supplier")
  const USER_MENU_ITEMS = [
    {
      label: t("menus.profile"),
      icon: <IconUserEdit size="1rem" />,
      onClick: () => navigate("/profile"),
    },
    {
      label: t("menus.invoices"),
      icon: <IconFileDollar size="1rem" />,
      onClick: () => navigate("/invoices"),
    },
    {
      label: t("menus.logout"),
      icon: <IconLogout size="1rem" />,
      onClick: () => logout(),
    },
  ]

  const adminItem: MenuItem | null = isAdmin
    ? {
        label: t("menus.admin"),
        icon: <IconAdjustments size="1rem" />,
        onClick: () => navigate("/admin"),
      }
    : null

  const menuItems = adminItem
    ? [
        ...USER_MENU_ITEMS.slice(0, -1),
        adminItem,
        USER_MENU_ITEMS[USER_MENU_ITEMS.length - 1],
      ]
    : USER_MENU_ITEMS

  if (!user) return null
  return (
    <BaseAppShell
      padding={0}
      layout="alt"
      navbar={{ width: "70px", breakpoint: 0 }}
      header={{ height: "4rem" }}
    >
      <AppHeader theme="brand">
        <AppLanguageMenu
          variant="dark"
          currentLocale={currentLocale.toUpperCase()}
          handleLanguageChange={async (locale) => {
            await i18n.changeLanguage(locale)
            setCurrentLocale(locale)
          }}
          languageItems={BRAND_LOCALES.map((locale) => ({
            label: findLanguage(locale.toUpperCase()),
            value: locale,
            icon: findLanguageFlag(locale.toUpperCase() as Locale),
          }))}
        />
        <UserMenu user={user} menuItems={menuItems} withAvatar={true} />
      </AppHeader>
      <AppNavBar navItems={navItems} settingsItems={SETTINGS_MENU_ITEMS} />
      <AppShellMain>
        <Outlet />
      </AppShellMain>
    </BaseAppShell>
  )
}
